import React, {
  MouseEvent,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { TAnalyticsFilters } from "../../shared/types/analyticsFilters.type";
import { TAnalyticView } from "../../shared/types/analyticView.type";
import {
  Autocomplete,
  AutocompleteCloseReason,
  Badge,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Divider,
  Fade,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Popover,
  Popper,
  PopperProps,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { t } from "i18next";
import { ExpandLess, ExpandMore, FilterAlt } from "@mui/icons-material";
import {
  capitalize,
  debounce,
  isEmpty,
  isNull,
  pick,
  uniq,
  without,
} from "lodash";
import { TAnalyticData } from "../../pages/analytics";
import toggleItemInArray from "../../shared/utils/arrays";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import { hands } from "../../enums/trackGroup.enums";
import { harness } from "../../enums/harness.enum";
import { useSelector } from "react-redux";
import { TuserAccountType } from "../../shared/types/user.type";
import { RootState } from "../../store";
import { Icon } from "@iconify/react";
import utc from "dayjs/plugin/utc";
import { styled } from "@mui/system";

import { LoadingButton } from "@mui/lab";
import MoreFilters, { FilterParamsValue } from "./MoreFilters";
import {
  FixedSizeList,
  ListChildComponentProps,
  VariableSizeList,
} from "react-window";
import FiltersSkeleton from "../exams/loader/FiltersSkeleton";
import DatePreselectoions from "./DatePreselectoions";
import { DATES_PRESELECTION } from "../../enums/datePreselection.enum";
import palette from "../../theme/palette";
import { useUserActivityLog } from "../../hooks/useUserActivityLog";
import { useLocales } from "../../hooks/useLocales";

const { RangePicker } = DatePicker;

dayjs.extend(utc);

type TFilters = {
  filters?: TAnalyticsFilters;
  currentView: TAnalyticView | null;
  analyticData: TAnalyticData;
  updateAnalyticData: (
    items: (number | null)[] | boolean,
    key: keyof TAnalyticData,
  ) => void;
  batchUpdate: (data: any) => void;
  resetFilters: () => void;
  saveViewNewParam: () => void;
  handleCustomValue: (values: FilterParamsValue[]) => void;
  loading: boolean;
  isTrialEnded: boolean;
  isEditEnabled: boolean;
  isViewUpdating: boolean;
  disableEdit?: boolean;
  openNewParamsModal: () => void;
};

type refProps = {
  clearInputs: () => void;
};

function Filters({
  openNewParamsModal,
  currentView,
  handleCustomValue,
  disableEdit,
  isEditEnabled,
  isViewUpdating,
  filters,
  saveViewNewParam,
  analyticData,
  updateAnalyticData,
  batchUpdate,
  loading,
  resetFilters,
  isTrialEnded,
}: TFilters) {
  const [open, setOpen] = useState(false);

  const { track } = useUserActivityLog();

  //Refs to access inputs from parent for each field
  const refHorse = React.useRef<refProps>(null);
  const refAge = React.useRef<refProps>(null);
  const refTrackName = React.useRef<refProps>(null);
  const refType = React.useRef<refProps>(null);
  const refCond = React.useRef<refProps>(null);
  const refSurface = React.useRef<refProps>(null);
  const refRider = React.useRef<refProps>(null);
  const refHand = React.useRef<refProps>(null);
  const refHarness = React.useRef<refProps>(null);
  const refTrainers = React.useRef<refProps>(null);
  const isManuallyClosing = useRef(false);

  const refs = [
    refHorse,
    refAge,
    refTrackName,
    refType,
    refCond,
    refSurface,
    refRider,
    refHand,
    refHarness,
    refTrainers,
  ];

  const [selectedPeriod, setSelectedPeriod] = useState<string | null>(null);

  const accountType = useSelector<RootState>(
    (state) => state.user.user?.type,
  ) as TuserAccountType | undefined;

  const updateFilters = (
    items: (number | null)[] | boolean,
    key: keyof TAnalyticData,
  ) => {
    updateAnalyticData(items, key);
  };

  const handleOpenChange = (isOpen: boolean) => {
    // If we manually closed, ignore the "true" event from the picker
    if (isManuallyClosing.current && isOpen) {
      isManuallyClosing.current = false; // reset flag
      return;
    }

    if (isOpen) {
      track("ANALYTICS_DATE_RANGE_FILTER_CLICKED");
    }

    setOpen(isOpen);
  };

  const onPeriodChange = (period: string | null) => {
    // Then update selected period
    if (!period) {
      batchUpdate({ date_start: null, date_end: null });
    } else {
      const find = DATES_PRESELECTION.find((p) => p.value === period);
      if (find) {
        batchUpdate({
          date_start: find.startDate,
          date_end: find.endDate,
        });
      }
      // Close the picker manually
      isManuallyClosing.current = true;
    }

    setOpen(false);
    setSelectedPeriod(period);
  };

  const clearFilters = () => {
    refs.map((ref) => ref.current?.clearInputs());
    resetFilters();
    setSelectedPeriod(null);
  };
  const getFullScreenContainer = () =>
    document.getElementById("analytics_content") ?? document.body;

  if (loading) {
    return <FiltersSkeleton />;
  }

  return (
    <Box
      display={"flex"}
      alignItems={"center"}
      gap={1}
      flexWrap={"wrap"}
      mt={2}
    >
      <Box
        display={"flex"}
        justifyContent={"space-between"}
        alignItems={"center"}
        flex={1}
        mb={1}
        gap={2}
        flexWrap={"wrap"}
      >
        <Typography variant="subtitle2">{t("filters")}</Typography>
        <Box display={"flex"} gap={1} alignItems={"center"}>
          {isEditEnabled && !disableEdit && (
            <Tooltip
              arrow
              title={t("button:saveParamsAndFilters")}
              placement="left"
            >
              <LoadingButton
                loading={isViewUpdating}
                onClick={saveViewNewParam}
                startIcon={<Icon icon="basil:save-outline"></Icon>}
                variant="contained"
              >
                {t("saveView")}
              </LoadingButton>
            </Tooltip>
          )}
          <Button
            onClick={clearFilters}
            variant="outlined"
            startIcon={<Icon icon="ic:round-clear-all"></Icon>}
          >
            {t("resetFilter")}
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {!disableEdit && (
          <Grid item md={2} sm={6} xs={12}>
            <ListExpander
              eventKey="ANALYTICS_HORSE_FILTER_CLICKED"
              ref={refHorse}
              loading={loading || isTrialEnded}
              objectKey="horses"
              updateFilters={updateFilters}
              checkedValues={analyticData.horses}
              allSelected={
                analyticData.horses.length === (filters?.horses.length ?? 0)
              }
              label={t("myHorses")}
              values={filters?.horses.map((i) => ({ id: i.id, name: i.name }))}
              acceptNull={false}
            ></ListExpander>
          </Grid>
        )}

        <Grid item md={2} sm={6} xs={12}>
          <ListExpander
            eventKey="ANALYTICS_HORSE_AGE_FILTER_CLICKED"
            ref={refAge}
            acceptNull
            loading={loading || isTrialEnded}
            objectKey="horse_ages"
            updateFilters={updateFilters}
            checkedValues={analyticData.horse_ages}
            allSelected={
              analyticData.horse_ages.length ===
              (filters?.horse_ages.length ?? 0) + 1
            }
            label={t("age")}
            values={filters?.horse_ages.map((i) => ({
              id: i,
              name: String(i),
            }))}
          ></ListExpander>
        </Grid>
        <Grid item md={2} sm={6} xs={12}>
          <ListExpander
            eventKey="ANALYTICS_TRACK_FILTER_CLICKED"
            ref={refTrackName}
            acceptNull
            loading={loading || isTrialEnded}
            objectKey="track_names"
            updateFilters={updateFilters}
            checkedValues={analyticData.track_names}
            allSelected={
              analyticData.track_names.length ===
              (filters?.track_names.length ?? 0) + 1
            }
            label={t("editTrainingForm:trackName:label")}
            values={filters?.track_names.map((i) => ({
              id: i.id,
              name: i.display_name,
            }))}
          ></ListExpander>
        </Grid>
        <Grid item md={2} sm={6} xs={12}>
          <ListExpander
            eventKey="ANALYTICS_TRAINING_TYPE_FILTER_CLICKED"
            ref={refType}
            acceptNull
            loading={loading || isTrialEnded}
            objectKey="training_types"
            updateFilters={updateFilters}
            checkedValues={analyticData.training_types}
            allSelected={
              analyticData.training_types.length ===
              (filters?.training_types.length ?? 0) + 1
            }
            label={t("editTrainingForm:trainingType:label")}
            values={filters?.training_types.map((i) => ({
              id: i.id,
              name: i.display_name,
            }))}
          ></ListExpander>
        </Grid>
        <Grid item md={2} sm={6} xs={12}>
          <ListExpander
            eventKey="ANALYTICS_TRACK_CONDITION_FILTER_CLICKED"
            ref={refCond}
            acceptNull
            loading={loading || isTrialEnded}
            objectKey="track_conditions"
            updateFilters={updateFilters}
            checkedValues={analyticData.track_conditions}
            allSelected={
              analyticData.track_conditions.length ===
              (filters?.track_conditions.length ?? 0) + 1
            }
            label={t("editTrainingForm:trackCondition:label")}
            values={filters?.track_conditions.map((i) => ({
              id: i.id,
              name: i.display_name,
            }))}
          ></ListExpander>
        </Grid>
        <Grid item md={2} sm={6} xs={12}>
          <ListExpander
            eventKey="ANALYTICS_TRACK_SURFACE_FILTER_CLICKED"
            ref={refSurface}
            acceptNull
            loading={loading || isTrialEnded}
            objectKey="track_surfaces"
            updateFilters={updateFilters}
            checkedValues={analyticData.track_surfaces}
            allSelected={
              analyticData.track_surfaces.length ===
              (filters?.track_surfaces.length ?? 0) + 1
            }
            label={t("editTrainingForm:trackSurface:label")}
            values={filters?.track_surfaces.map((i) => ({
              id: i.id,
              name: i.display_name,
            }))}
          ></ListExpander>
        </Grid>
        <Grid item md={2} sm={6} xs={12}>
          <ListExpander
            eventKey="ANALYTICS_RIDER_FILTER_CLICKED"
            ref={refRider}
            acceptNull
            loading={loading || isTrialEnded}
            objectKey="riders"
            updateFilters={updateFilters}
            checkedValues={analyticData.riders}
            allSelected={
              analyticData.riders.length === (filters?.riders.length ?? 0) + 1
            }
            label={t("editTrainingForm:riderName:label")}
            values={filters?.riders.map((i) => ({
              id: i.id,
              name: i.display_name,
            }))}
          ></ListExpander>
        </Grid>
        <Grid item md={2} sm={6} xs={12}>
          <ListExpander
            eventKey="ANALYTICS_HAND_FILTER_CLICKED"
            ref={refHand}
            acceptNull
            loading={loading || isTrialEnded}
            objectKey="hands"
            updateFilters={updateFilters}
            checkedValues={analyticData.hands}
            allSelected={analyticData.hands.length === (hands.length ?? 0) + 1}
            label={t("eap:table:headers:hand")}
            values={hands.map((i) => ({ id: i.value, name: t(i.key) }))}
          ></ListExpander>
        </Grid>
        {accountType === "TROT" && (
          <Grid item md={2} sm={6} xs={12}>
            <ListExpander
              eventKey="ANALYTICS_HARNESSED_MOUNTED_FILTER_CLICKED"
              ref={refHarness}
              loading={loading || isTrialEnded}
              objectKey="harnessed_mounteds"
              updateFilters={updateFilters}
              checkedValues={analyticData.harnessed_mounteds}
              allSelected={
                analyticData.harnessed_mounteds.length ===
                (harness.length ?? 0) + 1
              }
              label={t("qualification:harnessedMounted:harnessedMounted")}
              values={harness.map((i) => ({ id: i.value, name: t(i.key) }))}
            ></ListExpander>
          </Grid>
        )}
        <Grid item md={2} sm={6} xs={12}>
          <ListItem disableGutters dense>
            <RangePicker
              open={open}
              onOpenChange={handleOpenChange}
              placement="bottomRight"
              disabled={loading || isTrialEnded}
              onChange={(d, v) => {
                if (!d) {
                  onPeriodChange(null);
                  return;
                }
                batchUpdate({
                  date_start: d?.[0]?.utc(true).toISOString() ?? null,
                  date_end: d?.[1]?.utc(true).toISOString() ?? null,
                });
              }}
              getPopupContainer={() => getFullScreenContainer()}
              value={[
                analyticData.date_start ? dayjs(analyticData.date_start) : null,
                analyticData.date_end ? dayjs(analyticData.date_end) : null,
              ]}
              format="DD/MM/YYYY"
              className="custom-range-picker"
              renderExtraFooter={() => (
                <DatePreselectoions
                  selectedPeriod={selectedPeriod}
                  onPerodChange={onPeriodChange}
                />
              )}
            />
          </ListItem>
        </Grid>
        {/* If trainers length>1  */}

        {(filters?.trainers ?? []).length > 1 && (
          <Grid item md={2} sm={6} xs={12}>
            <ListExpander
              eventKey="ANALYTICS_TRAINER_FILTER_CLICKED"
              ref={refTrainers}
              acceptNull={false}
              loading={loading || isTrialEnded}
              objectKey="trainers"
              updateFilters={updateFilters}
              checkedValues={analyticData.trainers}
              allSelected={
                analyticData.trainers.length ===
                (filters?.trainers.length ?? 0) + 1
              }
              label={t("equi:registration:role:trainer")}
              values={filters?.trainers.map((i) => ({
                id: i.id as number,
                name: String(i.full_name),
              }))}
            ></ListExpander>
          </Grid>
        )}
        {!disableEdit && (
          <Grid item md={2} sm={6} xs={12} alignSelf={"center"}>
            <FormControlLabel
              disabled={loading || isTrialEnded}
              onChange={(_, checked) => {
                updateFilters(checked, "include_horses_out_of_training");
                track("ANALYTICS_OUT_OF_TRAINING_FILTER_CLICKED");
              }}
              checked={analyticData?.include_horses_out_of_training}
              control={<Checkbox />}
              label={t("eap:includeHorsesOutOfTraining")}
            />
          </Grid>
        )}

        {!disableEdit && (
          <Grid item md={2} sm={6} xs={12} alignSelf={"center"}>
            <FormControlLabel
              disabled={loading || isTrialEnded}
              onChange={(_, checked) => {
                updateFilters(checked, "only_reference_trainings");
                track("ANALYTICS_REFERENCE_TRAININGS_FILTER_CLICKED");
              }}
              checked={analyticData?.only_reference_trainings}
              control={<Checkbox />}
              label={t("eap:onlyReferenceTrainings")}
            />
          </Grid>
        )}
      </Grid>
      <MoreFilters
        handleSave={handleCustomValue}
        loading={loading}
        filters={filters}
        defaultViewCritearia={currentView?.insight_value_criterias}
        openNewParamsModal={openNewParamsModal}
      />
    </Box>
  );
}

const ListExpander = React.forwardRef<
  refProps,
  {
    label: string;
    values?: { id: number | string; name?: string }[];
    allSelected?: boolean;
    checkedValues: any[];
    updateFilters: (items: any[], key: keyof TAnalyticData) => void;
    objectKey: keyof TAnalyticData;
    loading: boolean;
    acceptNull?: boolean;
    eventKey: string;
  }
>(
  (
    {
      label,
      values,
      allSelected,
      checkedValues,
      updateFilters,
      objectKey,
      loading,
      acceptNull,
      eventKey,
    },
    ref,
  ) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [searchInput, setSearchInput] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const { track } = useUserActivityLog();
    const { isRtl } = useLocales();

    //Track user click on open popup
    useEffect(() => {
      if (anchorEl) {
        track(eventKey);
      }
    }, [anchorEl]);

    // Create a stable debounced function using useMemo or useCallback
    const debouncedSetSearchQuery = useMemo(
      () => debounce((query: string) => setSearchQuery(query), 300),
      [],
    );

    // Clean up debounce on unmount
    useEffect(() => {
      return () => {
        debouncedSetSearchQuery.cancel();
      };
    }, [debouncedSetSearchQuery]);

    // Handle input change
    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchInput(value); // Update immediate input value
      debouncedSetSearchQuery(value); // Debounced update for search query/filtering
    };

    const handleClick = (event: MouseEvent<HTMLElement>) => {
      if (loading) return;
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    const id = open ? "custom-popover" : undefined;

    useImperativeHandle(ref, () => ({
      clearInputs: () => {},
    }));

    const toggleSelectAll = (checked: boolean) => {
      if (!values) return;

      if (checked) {
        // Select all IDs + null if accepted
        const allIds = values.map((e) => e.id);
        const newChecked = acceptNull ? [...allIds, null] : allIds;
        updateFilters(newChecked, objectKey);
      } else {
        // Clear all
        updateFilters([], objectKey);
      }
    };

    const filteredValues = useMemo(() => {
      if (!values || isEmpty(values) || !searchQuery) return values;

      const lowerQuery = searchQuery.toLowerCase();

      return values.filter(
        (item) => (item.name ?? "").toLowerCase().indexOf(lowerQuery) !== -1,
      );
    }, [values, searchQuery]);

    const Row = ({ index, style }: ListChildComponentProps) => {
      const item = filteredValues?.[index];
      if (!item) return null;

      const isChecked = checkedValues.includes(item.id);

      const handleToggle = () => {
        const filter_items: any[] = [item.id];

        if (objectKey === "horse_ages" && checkedValues.includes(null)) {
          filter_items.push(null);
        }

        const newChecked = isChecked
          ? checkedValues.filter((id) => !filter_items.includes(id))
          : [...checkedValues, item.id];
        updateFilters(newChecked, objectKey);
      };

      return (
        <ListItem
          button
          dense
          style={{
            ...style,
          }}
          key={item.id}
          onClick={handleToggle}
        >
          <Checkbox
            edge="start"
            checked={isChecked}
            tabIndex={-1}
            disableRipple
          />
          <Typography px={1} variant="body2">
            {item.name ?? "Unnamed"}
          </Typography>
        </ListItem>
      );
    };

    return (
      <>
        <Box
          aria-describedby={id}
          onClick={handleClick}
          sx={{
            cursor: loading ? "wait" : "pointer",
            borderRadius: 1,
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            background: loading
              ? "hsla(0, 0%, 20%, 0.9)"
              : "hsla(0, 0%, 5%, .3)",
            transition: "border 0.2s ease-in-out",
            boxSizing: "border-box",
            position: "relative",
            border: "2px solid transparent", // base border
            "&:hover": {
              border: !loading ? "2px solid #A72C2C" : "2px solid transparent",
            },
          }}
        >
          <Badge
            invisible={isEmpty(checkedValues)}
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            overlap="rectangular"
            sx={{
              position: "absolute",
              top: -1,
              right: -1,
              zIndex: 2,
              pointerEvents: "none", // to ensure click events go through
            }}
            color="primary"
            variant="dot"
          ></Badge>
          <Typography
            noWrap
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: "150px", // or use '100%' with a parent constraint
            }}
            variant="subtitle2"
          >
            {label}
          </Typography>
          <Box display={"flex"} alignItems={"center"} gap={1}>
            {!isEmpty(checkedValues) && (
              <Chip
                size="small"
                icon={<FilterAlt />}
                label={checkedValues.length}
                sx={{
                  backgroundColor: "rgba(33, 150, 243, 0.3)", // soft blue tint
                  color: "#90caf9", // light blue text
                  fontWeight: 500,
                }}
              />
            )}
            <Icon icon="bx:chevron-down"></Icon>
          </Box>
        </Box>
        {open && (
          <Popover
            id={id}
            TransitionComponent={Fade}
            container={
              document.fullscreenElement ===
              document.getElementById("analytics_content")
                ? document.getElementById("analytics_content")
                : undefined
            }
            transitionDuration={150} // optional, default is 225ms
            PaperProps={{
              sx: {
                background: "hsla(0, 0%, 5%, .9)",
                maxWidth: 300,
                overflowY: "auto",
                p: 0.5,
                boxShadow: 3,
                borderRadius: 1,
                width: "auto",
                minWidth: anchorEl ? anchorEl.offsetWidth : undefined,
              },
            }}
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
          >
            <Box sx={{ width: "100%" }}>
              {/* TextField for search */}

              <TextField
                onChange={handleSearchChange}
                value={searchInput}
                size="small"
                label={t("search")}
                sx={{
                  m: 1,
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon icon="icons8:search" fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: !isEmpty(searchQuery) && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSearchInput("");
                          setSearchQuery("");
                        }}
                      >
                        <Icon icon={"ic:round-clear"} fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* Select all checkbox */}
              <ListItem
                onMouseDown={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
                selected={allSelected}
                button
                dense
              >
                <ListItemIcon>
                  <Checkbox
                    id="select-all-checkbox"
                    checked={allSelected}
                    edge="start"
                    onChange={(_, checked) => {
                      _.stopPropagation();
                      toggleSelectAll(checked);
                    }}
                  />
                </ListItemIcon>
                <Typography variant="body2">Select All</Typography>
              </ListItem>

              {/* Virtualized List */}
              <FixedSizeList
                height={300}
                width="100%"
                itemSize={60}
                style={{ paddingBottom: "60px", scrollBehavior: "smooth" }}
                itemCount={filteredValues?.length || 0}
              >
                {Row}
              </FixedSizeList>

              {/* Sticky N/A option */}
              {acceptNull && (
                <ListItem
                  button
                  dense
                  onClick={() => {
                    const naSelected = checkedValues.includes(null);
                    const newChecked = naSelected
                      ? checkedValues.filter((id) => id !== null)
                      : [...checkedValues, null];
                    updateFilters(newChecked, objectKey);
                  }}
                  sx={{
                    borderTop: "1px solid rgba(255,255,255,0.1)",
                    position: "absolute",
                    bottom: 0,
                    bgcolor: "hsla(0, 0%, 5%, 1)",
                    zIndex: 10,
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                >
                  <Checkbox
                    edge="start"
                    checked={checkedValues.includes(null)}
                    tabIndex={-1}
                    disableRipple
                  />
                  <Typography variant="body2">N/A</Typography>
                </ListItem>
              )}
            </Box>
          </Popover>
        )}
      </>
    );
  },
);

export default Filters;
